import { Link } from "react-router-dom";
import { IMG } from "@/data/images";
import { BellRing, Bath, ArrowUpFromLine, DoorOpen, Accessibility, Euro, ClipboardList, Hammer, PhoneCall, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";

const THEMEN = [
  {
    id: "hausnotruf",
    img: IMG.hausnotruf,
    imgAlt: "Seniorin mit Hausnotruf-Armband am Handgelenk",
    artikel: "/blog/hausnotruf-pflegekasse-kosten-zuschuss",
    icon: BellRing,
    badge: "Neu in der Beratung",
    title: "Hausnotruf",
    claim: "Sicherheit auf Knopfdruck – rund um die Uhr.",
    zahl: "25,50 €",
    zahlLabel: "übernimmt die Pflegekasse monatlich",
    zahlHinweis: "ab Pflegegrad 1, wenn Sie allein leben oder Hilfe nicht jederzeit erreichbar ist",
    punkte: [
      { icon: PhoneCall, text: "Notruf per Armband oder Halskette – Verbindung zur Zentrale in Sekunden" },
      { icon: ShieldCheck, text: "Hilfe wird organisiert: Angehörige, Pflegedienst oder Rettungsdienst" },
      { icon: Sparkles, text: "Auf Wunsch mit Sturzsensor, Schlüsselhinterlegung oder Rauchmelder" },
    ],
    schritte: ["Bedarf klären", "Antrag bei der Pflegekasse", "Anbieter auswählen", "Installation zu Hause"],
  },
  {
    id: "umbau",
    img: IMG.umbauDusche,
    imgAlt: "Barrierefreies Bad mit bodengleicher Dusche und Haltegriffen",
    artikel: "/blog/badumbau-zuschuss-pflegekasse-treppenlift",
    icon: Hammer,
    badge: "Neu in der Beratung",
    title: "Umbaumaßnahmen der Pflegekasse",
    claim: "Badumbau, Treppenlift & Co. – mit Zuschuss statt Eigenkosten.",
    zahl: "4.180 €",
    zahlLabel: "Zuschuss pro Maßnahme (§ 40 SGB XI)",
    zahlHinweis: "ab Pflegegrad 1 · bei mehreren Pflegebedürftigen im Haushalt bis zu 16.720 €",
    punkte: [
      { icon: Bath, text: "Badumbau: bodengleiche Dusche, Haltegriffe, unterfahrbares Waschbecken" },
      { icon: ArrowUpFromLine, text: "Treppenlift oder Rampe – sicher über Stufen und Schwellen" },
      { icon: DoorOpen, text: "Türverbreiterung, Umzug in eine barrierefreie Wohnung" },
    ],
    schritte: ["Beratung vor Ort", "Kostenvoranschlag einholen", "Antrag VOR Baubeginn", "Umsetzung & Abrechnung"],
  },
];

export default function BeratungsThemen({ variant = "cyan", ctaHref = "#kontakt" }) {
  const c = variant === "green"
    ? { accent: "text-green-700", bg: "bg-green-50", border: "border-green-200", solid: "bg-green-600", solidHover: "hover:bg-green-700", ring: "ring-green-100", soft: "bg-green-100 text-green-700" }
    : { accent: "text-cyan-700", bg: "bg-cyan-50", border: "border-cyan-200", solid: "bg-cyan-700", solidHover: "hover:bg-cyan-600", ring: "ring-cyan-100", soft: "bg-cyan-100 text-cyan-700" };

  return (
    <section className="py-16 lg:py-24 bg-white" id="beratungsthemen" data-testid="beratungsthemen-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded-full ${c.soft}`}><Sparkles className="h-3.5 w-3.5" />Neu: Wir beraten jetzt auch zu</span>
          <h2 className="mt-4 font-['Manrope'] text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">Hausnotruf & Umbaumaßnahmen der Pflegekasse</h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">Mehr Sicherheit und Selbständigkeit zu Hause – und die Pflegekasse zahlt mit. Wir klären Ihren Anspruch, helfen beim Antrag und begleiten Sie bis zur Umsetzung. Kostenfrei im Rahmen unserer Pflegeberatung.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {THEMEN.map((t) => (
            <article key={t.id} className={`relative bg-[#F8FAFC] border ${c.border} rounded-3xl overflow-hidden`} data-testid={`thema-${t.id}`}>
              <div className="h-52 sm:h-60 overflow-hidden"><img src={t.img} alt={t.imgAlt} className="w-full h-full object-cover" loading="lazy" data-testid={`thema-${t.id}-img`} /></div>
              <div className="relative p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className={`h-14 w-14 shrink-0 rounded-2xl ${c.solid} text-white flex items-center justify-center shadow-sm`}><t.icon className="h-7 w-7" /></div>
                  <div>
                    <h3 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900">{t.title}</h3>
                    <p className="text-sm sm:text-base text-slate-600 mt-1">{t.claim}</p>
                  </div>
                </div>

                <div className={`mt-6 bg-white rounded-2xl border ${c.border} p-5 flex items-center gap-5`} data-testid={`thema-${t.id}-zahl`}>
                  <div className={`h-12 w-12 shrink-0 rounded-xl ${c.soft} flex items-center justify-center`}><Euro className="h-6 w-6" /></div>
                  <div>
                    <p className={`font-['Manrope'] text-3xl sm:text-4xl font-extrabold ${c.accent} leading-none`}>{t.zahl}</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{t.zahlLabel}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{t.zahlHinweis}</p>
                  </div>
                </div>

                <ul className="mt-6 space-y-3">
                  {t.punkte.map((p) => (
                    <li key={p.text} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                      <div className={`h-8 w-8 shrink-0 rounded-lg bg-white border ${c.border} ${c.accent} flex items-center justify-center`}><p.icon className="h-4 w-4" /></div>
                      <span className="pt-1">{p.text}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7">
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-3 inline-flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5" />So läuft es ab</p>
                  <ol className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {t.schritte.map((s, i) => (
                      <li key={s} className="relative bg-white border border-slate-200 rounded-xl p-3 text-center">
                        <span className={`mx-auto mb-1.5 h-6 w-6 rounded-full ${c.solid} text-white text-xs font-bold flex items-center justify-center`}>{i + 1}</span>
                        <span className="text-[12px] sm:text-xs font-semibold text-slate-700 leading-tight block">{s}</span>
                        {i < t.schritte.length - 1 && <ArrowRight className="hidden sm:block absolute -right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />}
                      </li>
                    ))}
                  </ol>
                </div>
                <Link to={t.artikel} data-testid={`thema-${t.id}-artikel`} className={`mt-5 inline-flex items-center gap-1.5 text-sm font-semibold ${c.accent} hover:underline`}>Ausführlicher Ratgeber lesen<ArrowRight className="h-4 w-4" /></Link>
              </div>
            </article>
          ))}
        </div>

        <div className={`mt-8 rounded-2xl ${c.bg} border ${c.border} p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4`}>
          <div className="flex items-start gap-3">
            <Accessibility className={`h-6 w-6 ${c.accent} shrink-0 mt-0.5`} />
            <p className="text-sm sm:text-base text-slate-700"><strong className="text-slate-900">Wichtig:</strong> Den Antrag auf Umbauzuschuss immer <strong>vor</strong> Baubeginn stellen – wir übernehmen das gern mit Ihnen.</p>
          </div>
          <a href={ctaHref} data-testid="beratungsthemen-cta" className={`inline-flex items-center gap-2 h-12 px-6 rounded-xl ${c.solid} ${c.solidHover} text-white font-semibold whitespace-nowrap transition-colors`}>Jetzt beraten lassen<ArrowRight className="h-4 w-4" /></a>
        </div>
      </div>
    </section>
  );
}
